// Weight check-in helpers. Pure functions - no Firebase.
import { toDate } from './membershipFees';

export const WEIGHT_UNITS = { KG: 'kg', LB: 'lb' };

const LB_PER_KG = 2.2046226218;

const round1 = (value) => Math.round((value + Number.EPSILON) * 10) / 10;

/**
 * Parse a free-text weight such as "70", "70 kg", "154 lbs" or 70.
 * Returns null when no number can be found.
 */
export const parseWeightInput = (value) => {
  if (value === null || value === undefined || value === '') return null;

  if (typeof value === 'number') {
    return isNaN(value) ? null : { value, unit: WEIGHT_UNITS.KG };
  }

  const str = String(value).trim().toLowerCase();
  const amount = parseFloat(str.replace(/[^\d.]/g, ''));
  if (isNaN(amount) || amount <= 0) return null;

  const unit = /lb|pound/.test(str) ? WEIGHT_UNITS.LB : WEIGHT_UNITS.KG;
  return { value: amount, unit };
};

export const toKg = (value, unit) => {
  const amount = Number(value);
  if (!Number.isFinite(amount)) return null;
  return unit === WEIGHT_UNITS.LB ? round1(amount / LB_PER_KG) : round1(amount);
};

export const fromKg = (kg, unit) => {
  const amount = Number(kg);
  if (!Number.isFinite(amount)) return null;
  return unit === WEIGHT_UNITS.LB ? round1(amount * LB_PER_KG) : round1(amount);
};

export const formatWeight = (kg, unit = WEIGHT_UNITS.KG) => {
  const converted = fromKg(kg, unit);
  return converted === null ? '—' : `${converted} ${unit}`;
};

// Normalize a stored entry, tolerating older/partial shapes
const normalizeEntry = (entry, index) => {
  const weightKg =
    Number.isFinite(Number(entry.weightKg))
      ? Number(entry.weightKg)
      : toKg(entry.enteredValue ?? entry.weight, entry.unit || WEIGHT_UNITS.KG);

  if (!Number.isFinite(weightKg)) return null;

  return {
    ...entry,
    id: entry.id || `entry_${index}`,
    weightKg: round1(weightKg),
    unit: entry.unit || WEIGHT_UNITS.KG,
    measuredAt: entry.measuredAt || entry.date || entry.createdAt || null,
    notes: entry.notes || '',
    synthetic: Boolean(entry.synthetic)
  };
};

/**
 * Full weight series for a member, oldest first.
 *
 * The weight captured on the registration form is used as the baseline reading
 * when nothing has been recorded at or before that date, so existing members
 * get a starting point without any data migration.
 */
export const buildWeightSeries = (member, entries = []) => {
  const normalized = (Array.isArray(entries) ? entries : Object.values(entries || {}))
    .map(normalizeEntry)
    .filter(Boolean)
    .sort((a, b) => (toDate(a.measuredAt)?.getTime() || 0) - (toDate(b.measuredAt)?.getTime() || 0));

  const registrationWeight = parseWeightInput(member?.weight);
  const registrationDate = member?.registrationDate || member?.createdAt || null;

  if (!registrationWeight || !registrationDate) return normalized;

  const registrationTime = toDate(registrationDate)?.getTime();
  const hasBaseline =
    registrationTime === undefined ||
    normalized.some((entry) => (toDate(entry.measuredAt)?.getTime() || 0) <= registrationTime);

  if (hasBaseline) return normalized;

  const baseline = normalizeEntry(
    {
      id: 'registration_baseline',
      weightKg: toKg(registrationWeight.value, registrationWeight.unit),
      unit: registrationWeight.unit,
      measuredAt: registrationDate,
      notes: 'Recorded at registration',
      synthetic: true
    },
    0
  );

  return baseline ? [baseline, ...normalized] : normalized;
};

/**
 * Progress summary for a weight series (oldest first).
 * `change` is latest minus baseline, in kg.
 */
export const summarizeWeightProgress = (series = []) => {
  if (series.length === 0) {
    return {
      hasData: false,
      count: 0,
      baseline: null,
      latest: null,
      change: 0,
      changePercent: 0,
      direction: 'none',
      changeSincePrevious: null,
      min: null,
      max: null
    };
  }

  const baseline = series[0];
  const latest = series[series.length - 1];
  const previous = series.length > 1 ? series[series.length - 2] : null;

  const change = round1(latest.weightKg - baseline.weightKg);
  const changePercent = baseline.weightKg
    ? Math.round((change / baseline.weightKg) * 1000) / 10
    : 0;

  const weights = series.map((entry) => entry.weightKg);

  return {
    hasData: true,
    count: series.length,
    baseline,
    latest,
    change,
    changePercent,
    direction: change < 0 ? 'down' : change > 0 ? 'up' : 'flat',
    changeSincePrevious: previous ? round1(latest.weightKg - previous.weightKg) : null,
    min: Math.min(...weights),
    max: Math.max(...weights)
  };
};

// Chart geometry (viewBox units; the SVG scales to its container)
export const CHART = {
  width: 640,
  height: 200,
  padTop: 18,
  padRight: 18,
  padBottom: 30,
  padLeft: 46
};

/**
 * Map a weight series onto chart coordinates.
 * The y-axis is padded around the data rather than anchored at zero - weight
 * ranges are narrow, and this is a line chart, not a bar chart.
 */
export const buildChartModel = (series) => {
  if (!series || series.length === 0) return null;

  const plotWidth = CHART.width - CHART.padLeft - CHART.padRight;
  const plotHeight = CHART.height - CHART.padTop - CHART.padBottom;

  const weights = series.map((entry) => entry.weightKg);
  const rawMin = Math.min(...weights);
  const rawMax = Math.max(...weights);

  const span = rawMax - rawMin;
  const pad = span === 0 ? Math.max(1, rawMax * 0.02) : span * 0.15;
  const yMin = rawMin - pad;
  const yMax = rawMax + pad;

  const times = series.map((entry) => toDate(entry.measuredAt)?.getTime() || 0);
  const tMin = Math.min(...times);
  const tMax = Math.max(...times);
  const tSpan = tMax - tMin || 1;

  const points = series.map((entry, index) => {
    const time = toDate(entry.measuredAt)?.getTime() || 0;
    const x =
      series.length === 1
        ? CHART.padLeft + plotWidth / 2
        : CHART.padLeft + ((time - tMin) / tSpan) * plotWidth;
    const y = CHART.padTop + (1 - (entry.weightKg - yMin) / (yMax - yMin)) * plotHeight;
    return { ...entry, index, x: round1(x), y: round1(y) };
  });

  // Three recessive gridlines
  const ticks = [0, 0.5, 1].map((ratio) => ({
    value: round1(yMax - ratio * (yMax - yMin)),
    y: round1(CHART.padTop + ratio * plotHeight)
  }));

  return { points, ticks, yMin, yMax, baselineY: CHART.height - CHART.padBottom };
};

// Signed display string, e.g. "-3.5 kg" / "+1.2 kg"
export const formatChange = (changeKg, unit = WEIGHT_UNITS.KG) => {
  if (!Number.isFinite(changeKg) || changeKg === 0) return `0 ${unit}`;
  const converted = fromKg(Math.abs(changeKg), unit);
  return `${changeKg > 0 ? '+' : '−'}${converted} ${unit}`;
};
